import React, { useEffect, useState } from 'react';
import Layout from '../../elements/Layout';
import { getCollectionsReport } from '../../utils/api/api';
import { Line } from 'react-chartjs-2';
import { utils, writeFile } from 'xlsx';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const CollectionReports = () => {
    const [reports, setReports] = useState(null);
    const [chartView, setChartView] = useState('daily');

    useEffect(() => {
        fetchReports();
    }, []);

    const fetchReports = async () => {
        try {
            const response = await getCollectionsReport();
            setReports(response.invoice_collections);
        } catch (error) {
            console.error("Error fetching collection report:", error);
        }
    };

    const getChartData = () => {
        if (!reports) return null;

        let labels = [], expectedData = [], actualData = [];

        if (chartView === 'daily') {
            labels = reports.expected_collections_daily.map(item => item.day);
            expectedData = reports.expected_collections_daily.map(item => parseFloat(item.expectedCollection));
            actualData = reports.actual_collections_daily.map(item => item.actualCollection);
        } else if (chartView === 'weekly') {
            labels = reports.expected_collections_weekly.map(item => `W${item.week} ${item.year}`);
            expectedData = reports.expected_collections_weekly.map(item => parseFloat(item.expectedCollection));
            actualData = reports.actual_collections_weekly.map(item => item.actualCollection);
        } else if (chartView === 'monthly') {
            labels = reports.expected_collections_monthly.map(item => item.month);
            expectedData = reports.expected_collections_monthly.map(item => parseFloat(item.expectedCollection));
            actualData = reports.actual_collections_monthly.map(item => item.actualCollection);
        } else if (chartView === 'yearly') {
            labels = reports.expected_collections_yearly.map(item => item.year);
            expectedData = reports.expected_collections_yearly.map(item => parseFloat(item.expectedCollection));
            actualData = reports.actual_collections_yearly.map(item => item.actualCollection);
        }

        return {
            labels,
            datasets: [
                {
                    label: 'Expected Collections',
                    data: expectedData,
                    borderColor: 'rgba(30, 58, 138, 1)',
                    backgroundColor: 'rgba(30, 58, 138, 0.2)',
                    fill: true,
                    tension: 0.4,
                },
                {
                    label: 'Actual Collections',
                    data: actualData,
                    borderColor: 'rgba(239, 68, 68, 1)',
                    backgroundColor: 'rgba(239, 68, 68, 0.2)',
                    fill: true,
                    tension: 0.4,
                }
            ]
        };
    };

    const exportToExcel = () => {
        if (!reports) return;

        const data = reports.expected_collections_daily.map((item, index) => ({
            Date: item.day,
            "Expected Collection": item.expectedCollection,
            "Actual Collection": reports.actual_collections_daily[index]?.actualCollection || 0,
        }));

        const ws = utils.json_to_sheet(data);
        const wb = utils.book_new();
        utils.book_append_sheet(wb, ws, "Collection Report");
        writeFile(wb, "Collection_Report.xlsx");
    };

    const chartData = getChartData();

    return (
        <Layout text="Collection Reports">
            <div className="p-6 border rounded-xl ">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-[17px] font-medium text-primary">Invoice Collections</h2>
                    <button
                        onClick={exportToExcel}
                        className="px-6 py-2 bg-primary text-[14px] text-white rounded-md"
                    >
                        Export to Excel
                    </button>
                </div>

                <div className="mb-6 flex space-x-4">
                    {['daily', 'weekly', 'monthly', 'yearly'].map(view => (
                        <button
                            key={view}
                            onClick={() => setChartView(view)}
                            className={`px-4 py-2 rounded-md border transition ${chartView === view
                                ? 'bg-primary text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                        >
                            {view.charAt(0).toUpperCase() + view.slice(1)}
                        </button>
                    ))}
                </div>

                {chartData ? (
                    <Line
                        data={chartData}
                        options={{
                            responsive: true,
                            plugins: {
                                legend: { position: 'top' },
                                title: {
                                    display: true,
                                    text: `${chartView.charAt(0).toUpperCase() + chartView.slice(1)} Invoice Collections`,
                                    font: { size: 18 },
                                },
                                tooltip: {
                                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                    titleColor: '#fff',
                                    bodyColor: '#fff'
                                },
                            },
                            scales: {
                                x: {
                                    title: {
                                        display: true,
                                        text: chartView === 'daily' ? 'Date' : chartView === 'weekly' ? 'Week' : chartView === 'monthly' ? 'Month' : 'Year',
                                        font: { size: 14 },
                                    },
                                    grid: { display: false },
                                },
                                y: {
                                    title: {
                                        display: true,
                                        text: 'Amount (KES)',
                                        font: { size: 14 },
                                    },
                                    grid: { color: 'rgba(0, 0, 0, 0.1)' },
                                },
                            },
                        }}
                    />
                ) : (
                    <p className="text-center">Loading report data...</p>
                )}
            </div>
        </Layout>
    );
};

export default CollectionReports;
